"use client";

import { Link } from "@/i18n/navigation";
import { PhoneIcon, ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";

const CONTENT = {
  fr: {
    h1: "Vide maison à Uccle : ce qu'il faut savoir avant de se lancer",
    heroSubtitle:
      "Vous envisagez un vide maison à Uccle, pour une succession, une vente ou simplement pour faire de la place ? Voici les questions concrètes à se poser avant de démarrer, du déroulement de l'intervention aux erreurs à éviter.",
    ctaCall: "Appeler maintenant",
    ctaQuote: "Demander un devis gratuit",
    intro:
      "La commune la plus étendue de la Région bruxelloise réunit des réalités très différentes : villas avec jardin et dépendances, maisons unifamiliales, appartements en immeuble. Cette diversité influence directement l'organisation d'une intervention, du volume à prévoir jusqu'à l'accès pour le camion. Cet article répond aux questions concrètes que se posent la plupart des propriétaires ou des familles avant de se lancer : comment se déroule un vide maison, ce qui influence le prix et le délai, ce qu'il advient des objets de valeur, comment préparer l'accès, et les erreurs les plus fréquentes à éviter.",
    s1: {
      title: "Une même intervention, des réalités très différentes selon le bien",
      p1: "Contrairement à une commune au bâti plus homogène, un vide maison à Uccle ne correspond pas à un seul type d'intervention. Une grande propriété avec jardin, garage et parfois un atelier ou un abri de jardin demande une équipe et un temps sur place très différents d'un appartement dans un immeuble plus récent. Le point commun reste la méthode : repérage du bien, tri pièce par pièce, puis évacuation, quelle que soit la taille réelle du chantier.",
      p2: "Cette variété se retrouve aussi dans l'ancienneté des biens : certaines maisons sont occupées par la même famille depuis plusieurs générations, ce qui augmente souvent le volume à trier et la probabilité de trouver du mobilier ou des objets qui méritent d'être examinés avant l'évacuation.",
    },
    s2: {
      title: "Dans quelles situations envisage-t-on un vide maison à Uccle ?",
      introBefore: "Les demandes les plus fréquentes concernent une ",
      introLink: "succession",
      introAfter: " ou une mise en vente, mais d'autres situations reviennent régulièrement :",
      items: [
        "Un héritage impliquant une grande maison ou une villa à répartir entre plusieurs héritiers",
        "Un bien à libérer avant une vente ou une mise en location",
        "Un départ en maison de repos ou en résidence services",
        "Un désencombrement de cave, grenier ou garage accumulé depuis des années",
        "Un logement à vider rapidement après un déménagement ou une séparation",
      ],
    },
    s3: {
      title: "Comment se déroule concrètement une intervention",
      intro: "Le déroulement suit toujours la même logique, adaptée ensuite à la taille réelle du bien :",
      steps: [
        ["Premier contact", "Vous décrivez le bien, le volume approximatif et le délai souhaité, par téléphone ou via le formulaire en ligne."],
        ["Estimation", "Selon la taille du bien, quelques photos et une description suffisent, ou une visite sur place permet d'évaluer plus précisément l'accès et le volume réel, notamment pour une grande propriété avec dépendances."],
        ["Devis gratuit", "Vous recevez un devis détaillé, incluant la déduction des objets rachetés le cas échéant, sans engagement de votre part."],
        ["Intervention", "L'équipe procède pièce par pièce, en examinant au passage ce qui peut être racheté avant d'évacuer le reste."],
        ["Bien libéré", "Le bien est débarrassé et prêt pour l'étape suivante : vente, location ou remise des clés."],
      ],
      outro: "Pour un appartement, l'intervention peut se terminer en quelques heures. Pour une propriété avec garage, grenier et jardin, elle peut s'étaler sur une à deux journées, voire se planifier en plusieurs phases lorsque le volume ou le calendrier d'une succession le demande.",
    },
    s4: {
      title: "Ce qui influence le prix et le délai à Uccle",
      p1Before: "Il n'existe pas de tarif fixe : le prix dépend du volume réel à évacuer, du nombre de dépendances à traiter (garage, cave, grenier, abri de jardin), de l'accès au bien et des objets rachetés, qui viennent réduire la facture finale. Notre page ",
      p1Link: "tarifs",
      p1After: " détaille l'ensemble de ces facteurs.",
      p2: "L'accès pèse souvent plus lourd qu'on ne l'imagine dans l'organisation : une villa avec allée ou garage privé facilite généralement le chargement, tandis qu'un immeuble sans ascenseur ou un stationnement plus contraint peut demander une équipe plus nombreuse ou un créneau horaire adapté. Ce point se clarifie dès la demande de devis, pas le jour de l'intervention.",
    },
    ctaBox: {
      title: "Une idée du volume à évacuer ?",
      text: "Retrouvez les tarifs, situations fréquentes et la FAQ propres à votre quartier sur notre page dédiée à Uccle, ou demandez directement une estimation gratuite.",
      call: "Appeler maintenant",
      pageLink: "Voir la page Uccle",
    },
    s5: {
      title: "Trier avant d'évacuer : ce qui peut être valorisé",
      p1Before: "Avant toute évacuation, nous examinons ce qui a une valeur réelle : mobilier ancien, argenterie, tableaux, bibliothèques ou objets de collection, fréquents dans des maisons occupées depuis longtemps par une même famille. Ces objets sont proposés au rachat sur notre page ",
      p1Link1: "rachat d'objets de valeur",
      p1Mid: ", sans obligation d'accepter l'offre faite. Pour savoir reconnaître vous-même ce qui mérite d'être mis de côté avant notre passage, notre article ",
      p1Link2: "comment reconnaître un objet de valeur",
      p1After: " donne des repères concrets.",
      p2Before: "Le reste du contenu — mobilier sans valeur marchande, encombrants, déchets divers — est évacué vers les filières de tri adaptées. Dans une succession, la répartition des meubles entre héritiers se discute en amont : notre article sur le ",
      p2Link: "partage des meubles entre héritiers",
      p2After: " aborde ce sujet en détail.",
    },
    s6: {
      title: "Bien préparer l'intervention : nos conseils concrets",
      p1: "Quelques gestes simples facilitent nettement le déroulement de la journée : mettre de côté les documents personnels et objets à conserver, signaler un éventuel accès restreint (étages sans ascenseur, stationnement difficile) et prévenir le syndic si le bien est en copropriété. Pour une maison avec garage ou dépendances, il est utile de préciser dès le devis si ces espaces doivent être inclus dans l'intervention.",
      p2Before: "Ces points ne sont jamais une condition pour démarrer : beaucoup de nos interventions se déroulent sans préparation préalable. Notre ",
      p2Link: "checklist avant un vide maison",
      p2After: " reprend l'ensemble de ces points pour ceux qui souhaitent s'y préparer.",
    },
    s7: {
      title: "Faire appel à une équipe plutôt que de tout gérer seul",
      p1: "Vider soi-même une grande propriété demande du temps, un accès à un véhicule adapté et une bonne connaissance des filières d'évacuation. Confier l'ensemble du processus à un seul interlocuteur — estimation, tri, valorisation et évacuation — évite de devoir coordonner plusieurs prestataires soi-même, en particulier lorsque le bien comprend plusieurs dépendances ou que la situation familiale demande de la discrétion.",
    },
    s8: {
      title: "Les erreurs à éviter",
      items: [
        "Faire évacuer une partie du bien par ailleurs (benne, don rapide) avant d'avoir fait examiner les objets susceptibles d'être rachetés.",
        "Ne pas mentionner un accès restreint (absence d'ascenseur, stationnement difficile) au moment de la demande de devis.",
        "Sous-estimer le temps nécessaire pour les dépendances — garage, grenier, cave — par rapport aux seules pièces de vie.",
        "Ne pas prévenir le syndic lorsque le bien est en copropriété, ce qui peut retarder l'accès le jour convenu.",
        "Attendre que tous les héritiers se soient déplacés sur place avant de demander un premier devis, alors qu'une estimation à distance permet souvent d'avancer plus tôt.",
      ],
    },
    retenirTitle: "À retenir",
    retenir: [
      "Uccle réunit villas avec dépendances et appartements en immeuble : l'organisation s'adapte à chaque bien, pas à un modèle unique.",
      "Le prix dépend du volume réel, de l'accès et des objets rachetés — il n'y a pas de tarif fixe à la commune, mais le devis est gratuit.",
      "Les objets de valeur sont examinés avant toute évacuation ; le rachat, s'il y en a un, se déduit du devis final.",
      "L'accès (garage, allée, étages, syndic) mérite d'être signalé dès la demande de devis pour éviter les imprévus le jour J.",
      "Rien n'est obligatoire de votre côté : une intervention reste possible même sans tri préalable du bien.",
    ],
    s9: {
      title: "Pour aller plus loin",
      pBefore: "Retrouvez les situations les plus fréquentes, les repères par quartier et la FAQ propre à Uccle sur notre page ",
      pLink1: "vide maison Uccle",
      pMid: ". Pour un bien avec cave, grenier ou garage particulièrement chargés, notre page ",
      pLink2: "débarras cave, grenier et garage",
      pMid2: " détaille cette prestation, et notre page ",
      pLink3: "vide maison à Bruxelles, commune par commune",
      pAfter: " couvre l'ensemble des 19 communes desservies.",
    },
    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Combien de temps à l'avance faut-il réserver un vide maison à Uccle ?",
        a: "Il n'y a pas de délai imposé, mais mieux vaut anticiper de quelques semaines pour une grande propriété avec dépendances, notamment en période de déménagements ou lorsque plusieurs héritiers doivent d'abord se mettre d'accord. Pour un appartement ou une situation plus urgente, un créneau plus rapproché reste généralement possible : le mieux est de nous contacter dès que la date de vente, de fin de bail ou de remise des clés est connue, afin d'ajuster le planning en conséquence.",
      },
      {
        q: "Faut-il une autorisation pour stationner le camion devant la maison ?",
        a: "Cela dépend de la rue et du type de stationnement disponible. Devant une villa avec allée ou garage privé, aucune démarche n'est nécessaire. Le long d'une voirie plus fréquentée, une autorisation de stationnement auprès de la commune peut simplifier le chargement, en particulier si le bien ne dispose pas d'un accès direct. Nous vous conseillons de signaler ce point dès le devis afin d'évaluer ensemble si une telle démarche est utile pour votre adresse précise.",
      },
      {
        q: "Comment estimer soi-même le volume à débarrasser avant de demander un devis ?",
        a: "Un repère simple consiste à compter le nombre de pièces pleines, en distinguant les pièces de vie meublées des espaces de stockage comme la cave, le grenier ou le garage, souvent plus volumineux qu'on ne l'imagine. Quelques photos par pièce, envoyées avec une description du nombre d'étages et de l'accès, permettent déjà d'établir une première estimation. Pour un bien important, une visite sur place reste la méthode la plus fiable avant de confirmer un devis définitif.",
      },
      {
        q: "Faut-il trier vous-même avant votre passage, ou peut-on tout laisser en l'état ?",
        a: "Rien n'est obligatoire de votre côté : nous pouvons intervenir même si rien n'a été trié au préalable, ce qui est fréquent pour une maison occupée depuis plusieurs décennies. Si vous avez le temps, séparer ce que vous souhaitez conserver facilite l'organisation du jour J, mais ce n'est pas une condition pour obtenir un devis ni pour que l'intervention se déroule normalement. Le tri des objets de valeur reste de toute façon assuré par l'équipe sur place.",
      },
      {
        q: "Comment se passe l'estimation quand plusieurs héritiers sont concernés ?",
        a: "Lorsque plusieurs héritiers doivent se prononcer sur le sort des meubles, nous présentons les objets identifiés comme potentiellement rachetables avant toute décision d'évacuation, ce qui laisse le temps à la famille de se concerter. Le montant proposé, s'il y en a un, peut être communiqué à l'ensemble des personnes concernées avant d'être accepté. Cette étape prend parfois plus de temps qu'une décision individuelle, ce que nous intégrons naturellement dans le calendrier de l'intervention.",
      },
      {
        q: "Le tri des objets rallonge-t-il le prix ou le délai de l'intervention ?",
        a: "L'examen des objets susceptibles d'être rachetés fait partie intégrante de notre méthode et n'entraîne pas de frais supplémentaire : il est inclus dans le déroulement normal de l'intervention. À l'inverse, un rachat éventuel vient en déduction du devis final. Le délai varie surtout selon le volume réel du bien et son accès, pas selon la présence d'objets à examiner, qui se fait au fur et à mesure de l'évacuation plutôt qu'en une étape séparée.",
      },
      {
        q: "Le jardin ou les extérieurs sont-ils compris dans un vide maison ?",
        a: "Un vide maison porte sur le contenu du bien : pièces de vie, cave, grenier, garage et dépendances comme un abri de jardin. L'entretien du jardin lui-même — tonte, taille, évacuation de déchets verts — n'en fait pas partie et relève d'un autre type de prestataire. Si le terrain contient du mobilier de jardin, des outils ou des objets entreposés en extérieur, ceux-ci sont en revanche pris en compte dans l'estimation, comme le reste du bien.",
      },
    ],
    finalCta: {
      title: "Prêt à obtenir votre devis ?",
      text: "Contactez-nous pour une estimation gratuite et sans engagement.",
      quote: "Devis gratuit",
    },
  },
  nl: {
    h1: "Huis leegmaken in Ukkel: wat u moet weten vóór u begint",
    heroSubtitle:
      "Overweegt u een huis leegmaken in Ukkel, voor een nalatenschap, een verkoop of gewoon om plaats te maken? Hier zijn de concrete vragen die u zich moet stellen vóór u begint, van het verloop van de interventie tot de te vermijden fouten.",
    ctaCall: "Bel nu",
    ctaQuote: "Vraag een gratis offerte aan",
    intro:
      "De meest uitgestrekte gemeente van het Brussels Gewest verenigt zeer verschillende realiteiten: villa's met tuin en bijgebouwen, eengezinswoningen, appartementen in gebouwen. Deze diversiteit beïnvloedt rechtstreeks de organisatie van een interventie, van het te voorziene volume tot de toegang voor de vrachtwagen. Dit artikel beantwoordt de concrete vragen die de meeste eigenaars of families zich stellen vóór ze beginnen: hoe verloopt een huis leegmaken, wat beïnvloedt de prijs en de termijn, wat gebeurt er met waardevolle voorwerpen, hoe bereidt u de toegang voor, en wat zijn de meest voorkomende fouten om te vermijden.",
    s1: {
      title: "Eenzelfde interventie, zeer verschillende realiteiten naargelang het pand",
      p1: "In tegenstelling tot een gemeente met een meer homogene bebouwing, komt een huis leegmaken in Ukkel niet overeen met één enkel type interventie. Een groot eigendom met tuin, garage en soms een atelier of tuinhuis vergt een heel ander team en een andere tijd ter plaatse dan een appartement in een recenter gebouw. Het gemeenschappelijke punt blijft de methode: het pand verkennen, kamer per kamer sorteren, en dan afvoeren, ongeacht de werkelijke omvang van de klus.",
      p2: "Deze verscheidenheid vindt men ook terug in de ouderdom van de panden: sommige huizen worden al meerdere generaties door dezelfde familie bewoond, wat vaak het te sorteren volume verhoogt en de kans vergroot om meubilair of voorwerpen te vinden die vóór de afvoer bekeken moeten worden.",
    },
    s2: {
      title: "In welke situaties overweegt men een huis leegmaken in Ukkel?",
      introBefore: "De meest voorkomende aanvragen betreffen een ",
      introLink: "nalatenschap",
      introAfter: " of een verkoop, maar ook andere situaties komen regelmatig terug:",
      items: [
        "Een erfenis met een groot huis of een villa te verdelen tussen meerdere erfgenamen",
        "Een pand te ontruimen vóór een verkoop of een verhuring",
        "Een vertrek naar een rusthuis of een assistentiewoning",
        "Een al jarenlang volgestouwde kelder, zolder of garage opruimen",
        "Een woning die snel leeggemaakt moet worden na een verhuizing of een scheiding",
      ],
    },
    s3: {
      title: "Hoe verloopt een interventie concreet",
      intro: "Het verloop volgt altijd dezelfde logica, vervolgens aangepast aan de werkelijke grootte van het pand:",
      steps: [
        ["Eerste contact", "U beschrijft het pand, het geschatte volume en de gewenste termijn, telefonisch of via het online formulier."],
        ["Schatting", "Naargelang de grootte van het pand volstaan enkele foto's en een beschrijving, of laat een bezoek ter plaatse toe de toegang en het werkelijke volume nauwkeuriger in te schatten, met name voor een groot eigendom met bijgebouwen."],
        ["Gratis offerte", "U ontvangt een gedetailleerde offerte, inclusief de aftrek van ingekochte voorwerpen indien van toepassing, zonder enige verplichting van uw kant."],
        ["Interventie", "Het team werkt kamer per kamer, waarbij onderweg wordt bekeken wat ingekocht kan worden vóór de rest wordt afgevoerd."],
        ["Pand vrijgemaakt", "Het pand is ontruimd en klaar voor de volgende stap: verkoop, verhuring of sleuteloverdracht."],
      ],
      outro: "Voor een appartement kan de interventie in enkele uren afgerond zijn. Voor een eigendom met garage, zolder en tuin kan het één tot twee dagen duren, of zelfs in meerdere fases gepland worden wanneer het volume of de planning van een nalatenschap dat vereist.",
    },
    s4: {
      title: "Wat de prijs en de termijn in Ukkel beïnvloedt",
      p1Before: "Er bestaat geen vast tarief: de prijs hangt af van het werkelijke af te voeren volume, het aantal te behandelen bijgebouwen (garage, kelder, zolder, tuinhuis), de toegang tot het pand en de ingekochte voorwerpen, die de eindfactuur verlagen. Onze pagina ",
      p1Link: "tarieven",
      p1After: " beschrijft al deze factoren in detail.",
      p2: "De toegang weegt vaak zwaarder door dan men denkt bij de organisatie: een villa met oprit of privégarage vergemakkelijkt doorgaans het laden, terwijl een gebouw zonder lift of een beperktere parkeermogelijkheid een groter team of een aangepast tijdslot kan vereisen. Dit punt wordt bij de offerteaanvraag verduidelijkt, niet op de dag van de interventie.",
    },
    ctaBox: {
      title: "Een idee van het af te voeren volume?",
      text: "Vind de tarieven, veelvoorkomende situaties en de FAQ eigen aan uw buurt op onze pagina gewijd aan Ukkel, of vraag rechtstreeks een gratis schatting aan.",
      call: "Bel nu",
      pageLink: "Bekijk de pagina Ukkel",
    },
    s5: {
      title: "Sorteren vóór de afvoer: wat gevaloriseerd kan worden",
      p1Before: "Vóór elke afvoer bekijken we wat echte waarde heeft: antiek meubilair, zilverwerk, schilderijen, bibliotheken of verzamelvoorwerpen, vaak aanwezig in huizen die al lang door dezelfde familie bewoond worden. Deze voorwerpen worden voorgesteld voor inkoop op onze pagina ",
      p1Link1: "inkoop van waardevolle voorwerpen",
      p1Mid: ", zonder verplichting om het gedane aanbod te aanvaarden. Om zelf te leren herkennen wat het waard is om apart te zetten vóór ons bezoek, geeft ons artikel ",
      p1Link2: "hoe een waardevol voorwerp herkennen",
      p1After: " concrete richtlijnen.",
      p2Before: "De rest van de inhoud — meubilair zonder marktwaarde, grofvuil, diverse afval — wordt afgevoerd naar de gepaste sorteerkanalen. Bij een nalatenschap wordt de verdeling van meubels tussen erfgenamen vooraf besproken: ons artikel over de ",
      p2Link: "verdeling van meubels tussen erfgenamen",
      p2After: " behandelt dit onderwerp in detail.",
    },
    s6: {
      title: "De interventie goed voorbereiden: onze concrete tips",
      p1: "Enkele eenvoudige handelingen vergemakkelijken het verloop van de dag aanzienlijk: persoonlijke documenten en te bewaren voorwerpen apart zetten, een eventuele beperkte toegang signaleren (verdiepingen zonder lift, moeilijk parkeren) en de syndicus verwittigen als het pand in mede-eigendom is. Voor een huis met garage of bijgebouwen is het nuttig om al bij de offerte te preciseren of deze ruimtes in de interventie opgenomen moeten worden.",
      p2Before: "Deze punten zijn nooit een voorwaarde om te starten: veel van onze interventies verlopen zonder voorafgaande voorbereiding. Onze ",
      p2Link: "checklist vóór een huis leegmaken",
      p2After: " herneemt al deze punten voor wie zich hierop wil voorbereiden.",
    },
    s7: {
      title: "Een team inschakelen in plaats van alles zelf te regelen",
      p1: "Een groot eigendom zelf leegmaken vergt tijd, toegang tot een geschikt voertuig en een goede kennis van de afvoerkanalen. Het volledige proces aan één contactpersoon toevertrouwen — schatting, sortering, valorisatie en afvoer — vermijdt dat u zelf meerdere dienstverleners moet coördineren, in het bijzonder wanneer het pand meerdere bijgebouwen omvat of wanneer de familiale situatie discretie vereist.",
    },
    s8: {
      title: "De te vermijden fouten",
      items: [
        "Een deel van het pand elders laten afvoeren (container, snelle schenking) vóór de voorwerpen die mogelijk ingekocht kunnen worden te hebben laten bekijken.",
        "Een beperkte toegang (geen lift, moeilijk parkeren) niet vermelden bij de offerteaanvraag.",
        "De nodige tijd voor de bijgebouwen — garage, zolder, kelder — onderschatten ten opzichte van enkel de leefruimtes.",
        "De syndicus niet verwittigen wanneer het pand in mede-eigendom is, wat de toegang op de afgesproken dag kan vertragen.",
        "Wachten tot alle erfgenamen zich ter plaatse begeven hebben vóór een eerste offerte aan te vragen, terwijl een schatting op afstand vaak toelaat vroeger vooruitgang te boeken.",
      ],
    },
    retenirTitle: "Wat u moet onthouden",
    retenir: [
      "Ukkel verenigt villa's met bijgebouwen en appartementen in gebouwen: de organisatie past zich aan elk pand aan, niet aan één vast model.",
      "De prijs hangt af van het werkelijke volume, de toegang en de ingekochte voorwerpen — er is geen vast gemeentetarief, maar de offerte is gratis.",
      "Waardevolle voorwerpen worden vóór elke afvoer onderzocht; de inkoop, indien van toepassing, wordt van de eindofferte afgetrokken.",
      "De toegang (garage, oprit, verdiepingen, syndicus) verdient het om al bij de offerteaanvraag gesignaleerd te worden om onvoorziene zaken op de grote dag te vermijden.",
      "Niets is verplicht van uw kant: een interventie blijft mogelijk, zelfs zonder voorafgaande sortering van het pand.",
    ],
    s9: {
      title: "Voor meer informatie",
      pBefore: "Vind de meest voorkomende situaties, de aandachtspunten per buurt en de FAQ eigen aan Ukkel op onze pagina ",
      pLink1: "huis leegmaken Ukkel",
      pMid: ". Voor een pand met een bijzonder volgestouwde kelder, zolder of garage beschrijft onze pagina ",
      pLink2: "kelder, zolder en garage ontruimen",
      pMid2: " deze dienst, en onze pagina ",
      pLink3: "huis leegmaken in Brussel, gemeente per gemeente",
      pAfter: " dekt alle 19 bediende gemeenten.",
    },
    faqTitle: "Veelgestelde vragen",
    faq: [
      {
        q: "Hoeveel tijd op voorhand moet ik een huis leegmaken in Ukkel reserveren?",
        a: "Er is geen opgelegde termijn, maar het is beter enkele weken vooraf te plannen voor een groot eigendom met bijgebouwen, met name tijdens verhuisperiodes of wanneer meerdere erfgenamen het eerst eens moeten worden. Voor een appartement of een dringendere situatie blijft een korter tijdslot doorgaans mogelijk: het beste is ons te contacteren zodra de verkoopdatum, het einde van de huur of de sleuteloverdracht bekend is, om de planning dienovereenkomstig aan te passen.",
      },
      {
        q: "Is een vergunning nodig om de vrachtwagen voor het huis te parkeren?",
        a: "Dit hangt af van de straat en het type beschikbare parkeergelegenheid. Voor een villa met oprit of privégarage is geen enkele stap nodig. Langs een drukkere weg kan een parkeervergunning bij de gemeente het laden vergemakkelijken, met name als het pand geen rechtstreekse toegang heeft. Wij raden aan dit punt al bij de offerte te signaleren om samen te evalueren of zo'n stap nuttig is voor uw specifieke adres.",
      },
      {
        q: "Hoe schat ik zelf het af te voeren volume in vóór ik een offerte aanvraag?",
        a: "Een eenvoudig richtpunt bestaat erin het aantal volle kamers te tellen, met een onderscheid tussen de gemeubelde leefruimtes en de opslagruimtes zoals de kelder, de zolder of de garage, die vaak omvangrijker zijn dan men denkt. Enkele foto's per kamer, verstuurd met een beschrijving van het aantal verdiepingen en de toegang, laten al toe een eerste schatting op te stellen. Voor een groot pand blijft een bezoek ter plaatse de meest betrouwbare methode vóór een definitieve offerte te bevestigen.",
      },
      {
        q: "Moet ik zelf sorteren vóór uw bezoek, of kan ik alles laten zoals het is?",
        a: "Niets is verplicht van uw kant: wij kunnen tussenkomen zelfs als vooraf niets gesorteerd is, wat vaak voorkomt bij een huis dat al meerdere decennia bewoond wordt. Als u de tijd heeft, vergemakkelijkt het scheiden van wat u wilt bewaren de organisatie van de grote dag, maar dit is geen voorwaarde om een offerte te krijgen of om de interventie normaal te laten verlopen. Het sorteren van waardevolle voorwerpen wordt hoe dan ook door het team ter plaatse verzekerd.",
      },
      {
        q: "Hoe verloopt de schatting wanneer meerdere erfgenamen betrokken zijn?",
        a: "Wanneer meerdere erfgenamen zich moeten uitspreken over het lot van de meubels, presenteren wij de als potentieel inkoopbaar geïdentificeerde voorwerpen vóór elke afvoerbeslissing, wat de familie tijd geeft om te overleggen. Het voorgestelde bedrag, indien van toepassing, kan aan alle betrokken personen meegedeeld worden vóór het aanvaard wordt. Deze stap neemt soms meer tijd in beslag dan een individuele beslissing, wat wij vanzelfsprekend in de planning van de interventie integreren.",
      },
      {
        q: "Verlengt het sorteren van voorwerpen de prijs of de termijn van de interventie?",
        a: "Het onderzoeken van potentieel inkoopbare voorwerpen maakt integraal deel uit van onze methode en brengt geen extra kosten met zich mee: het is inbegrepen in het normale verloop van de interventie. Omgekeerd wordt een eventuele inkoop van de eindofferte afgetrokken. De termijn varieert vooral naargelang het werkelijke volume van het pand en de toegang, niet naargelang de aanwezigheid van te onderzoeken voorwerpen, wat gebeurt naarmate de afvoer vordert eerder dan als een aparte stap.",
      },
      {
        q: "Zijn de tuin of de buitenruimtes inbegrepen in een huis leegmaken?",
        a: "Een huis leegmaken betreft de inhoud van het pand: leefruimtes, kelder, zolder, garage en bijgebouwen zoals een tuinhuis. Het onderhoud van de tuin zelf — maaien, snoeien, afvoer van groenafval — maakt hier geen deel van uit en behoort tot een ander type dienstverlener. Als het terrein tuinmeubilair, gereedschap of buiten opgeslagen voorwerpen bevat, worden deze daarentegen wel meegenomen in de schatting, zoals de rest van het pand.",
      },
    ],
    finalCta: {
      title: "Klaar om uw offerte aan te vragen?",
      text: "Contacteer ons voor een gratis en vrijblijvende schatting.",
      quote: "Gratis offerte",
    },
  },
  en: {
    h1: "House clearance in Uccle: what to know before you start",
    heroSubtitle:
      "Considering a house clearance in Uccle, for an estate settlement, a sale, or simply to make some space? Here are the practical questions to ask yourself before starting, from how the intervention unfolds to the mistakes to avoid.",
    ctaCall: "Call now",
    ctaQuote: "Request a free quote",
    intro:
      "The largest commune in the Brussels Region brings together very different realities: villas with gardens and outbuildings, single-family houses, apartments in buildings. This diversity directly affects how an intervention is organised, from the volume to expect to truck access. This article answers the practical questions most owners or families ask themselves before starting: how a house clearance unfolds, what affects the price and timeline, what happens to valuable items, how to prepare access, and the most common mistakes to avoid.",
    s1: {
      title: "The same intervention, very different realities depending on the property",
      p1: "Unlike a commune with more uniform building stock, a house clearance in Uccle doesn't correspond to just one type of intervention. A large property with a garden, garage and sometimes a workshop or garden shed requires a very different team and time on site than an apartment in a more recent building. What they have in common is the method: surveying the property, sorting room by room, then removal, whatever the actual scale of the job.",
      p2: "This variety also shows up in how old the properties are: some houses have been occupied by the same family for several generations, which often increases the volume to sort and the likelihood of finding furniture or items worth examining before removal.",
    },
    s2: {
      title: "In which situations do people consider a house clearance in Uccle?",
      introBefore: "The most common requests relate to an ",
      introLink: "estate settlement",
      introAfter: " or a sale, but other situations come up regularly too:",
      items: [
        "An inheritance involving a large house or villa to be divided between several heirs",
        "A property to be vacated before a sale or rental",
        "A move to a care home or assisted-living residence",
        "Clearing out a cellar, attic or garage cluttered for years",
        "A home to be cleared quickly after a move or a separation",
      ],
    },
    s3: {
      title: "How an intervention actually unfolds",
      intro: "The process always follows the same logic, then adapted to the property's actual size:",
      steps: [
        ["Initial contact", "You describe the property, the approximate volume and the desired timeframe, by phone or via the online form."],
        ["Estimate", "Depending on the property's size, a few photos and a description are enough, or an on-site visit allows for a more precise assessment of access and actual volume, particularly for a large property with outbuildings."],
        ["Free quote", "You receive a detailed quote, including the deduction for any bought-back items where applicable, with no obligation on your part."],
        ["Intervention", "The team works room by room, checking along the way what can be bought back before removing the rest."],
        ["Property cleared", "The property is cleared and ready for the next step: sale, rental, or key handover."],
      ],
      outro: "For an apartment, the intervention can be finished in a few hours. For a property with a garage, attic and garden, it can span one to two days, or even be planned over several phases when the volume or the timeline of an estate settlement requires it.",
    },
    s4: {
      title: "What affects the price and timeline in Uccle",
      p1Before: "There's no fixed rate: the price depends on the actual volume to be removed, the number of outbuildings to deal with (garage, cellar, attic, garden shed), access to the property, and any bought-back items, which reduce the final bill. Our ",
      p1Link: "prices",
      p1After: " page details all these factors.",
      p2: "Access often weighs more heavily than you'd think in the organisation: a villa with a driveway or private garage usually makes loading easier, while a building with no lift or more constrained parking may require a larger team or an adapted time slot. This is clarified as soon as you request a quote, not on the day of the intervention.",
    },
    ctaBox: {
      title: "Have an idea of the volume to remove?",
      text: "Find the prices, common situations and FAQ specific to your neighbourhood on our page dedicated to Uccle, or request a free estimate directly.",
      call: "Call now",
      pageLink: "See the Uccle page",
    },
    s5: {
      title: "Sorting before removal: what can be valued",
      p1Before: "Before any removal, we look at what has real value: antique furniture, silverware, paintings, libraries or collectable items, often found in homes occupied for a long time by the same family. These items are offered for buyback on our ",
      p1Link1: "buyback of valuable items",
      p1Mid: " page, with no obligation to accept the offer made. To learn how to recognise yourself what's worth setting aside before our visit, our article ",
      p1Link2: "how to recognise a valuable item",
      p1After: " gives concrete pointers.",
      p2Before: "The rest of the contents — furniture with no market value, bulky items, various waste — is removed to the appropriate sorting channels. In an estate settlement, dividing furniture between heirs is discussed beforehand: our article on ",
      p2Link: "dividing furniture between heirs",
      p2After: " covers this topic in detail.",
    },
    s6: {
      title: "Preparing the intervention well: our practical advice",
      p1: "A few simple steps make the day run noticeably more smoothly: setting aside personal documents and items to keep, flagging any restricted access (floors with no lift, difficult parking) and notifying the managing agent if the property is co-owned. For a house with a garage or outbuildings, it's useful to specify at the quote stage whether these spaces should be included in the intervention.",
      p2Before: "These points are never a condition for starting: many of our interventions take place with no prior preparation. Our ",
      p2Link: "checklist before a house clearance",
      p2After: " covers all these points for anyone who wants to prepare.",
    },
    s7: {
      title: "Calling on a team rather than handling everything yourself",
      p1: "Clearing a large property yourself takes time, access to a suitable vehicle and good knowledge of removal channels. Handing over the whole process to a single point of contact — estimate, sorting, valuation and removal — avoids having to coordinate several providers yourself, particularly when the property includes several outbuildings or when the family situation calls for discretion.",
    },
    s8: {
      title: "Mistakes to avoid",
      items: [
        "Having part of the property removed elsewhere (skip, quick donation) before having the potentially valuable items looked at.",
        "Not mentioning restricted access (no lift, difficult parking) when requesting a quote.",
        "Underestimating the time needed for outbuildings — garage, attic, cellar — compared to the living areas alone.",
        "Not notifying the managing agent when the property is co-owned, which can delay access on the agreed day.",
        "Waiting for all heirs to visit the property in person before requesting a first quote, when a remote estimate often allows things to move forward earlier.",
      ],
    },
    retenirTitle: "Key takeaways",
    retenir: [
      "Uccle brings together villas with outbuildings and apartments in buildings: the organisation adapts to each property, not a single model.",
      "The price depends on the actual volume, access and any bought-back items — there's no fixed commune-wide rate, but the quote is free.",
      "Valuable items are examined before any removal; buyback, if any, is deducted from the final quote.",
      "Access (garage, driveway, floors, managing agent) is worth flagging as soon as you request a quote, to avoid surprises on the day.",
      "Nothing is required on your part: an intervention remains possible even without prior sorting of the property.",
    ],
    s9: {
      title: "To find out more",
      pBefore: "Find the most common situations, neighbourhood-specific pointers and the FAQ specific to Uccle on our ",
      pLink1: "house clearance Uccle",
      pMid: " page. For a property with a particularly cluttered cellar, attic or garage, our ",
      pLink2: "cellar, attic and garage clearance",
      pMid2: " page details this service, and our ",
      pLink3: "house clearance in Brussels, commune by commune",
      pAfter: " page covers all 19 communes we serve.",
    },
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "How far in advance should I book a house clearance in Uccle?",
        a: "There's no imposed lead time, but it's best to plan a few weeks ahead for a large property with outbuildings, particularly during moving season or when several heirs first need to agree. For an apartment or a more urgent situation, a shorter lead time is usually still possible: it's best to contact us as soon as the sale date, end of lease or key handover is known, so we can adjust the schedule accordingly.",
      },
      {
        q: "Do I need a permit to park the truck outside the house?",
        a: "This depends on the street and the type of parking available. In front of a villa with a driveway or private garage, no steps are needed. Along a busier road, a parking permit from the commune can make loading easier, particularly if the property has no direct access. We recommend flagging this when requesting a quote, so we can assess together whether such a step is useful for your specific address.",
      },
      {
        q: "How can I estimate the volume to be cleared myself before requesting a quote?",
        a: "A simple rule of thumb is to count the number of full rooms, distinguishing furnished living areas from storage spaces like the cellar, attic or garage, which are often bulkier than you'd imagine. A few photos per room, sent along with a description of the number of floors and access, are already enough to produce a first estimate. For a large property, an on-site visit remains the most reliable method before confirming a final quote.",
      },
      {
        q: "Do I need to sort things myself before your visit, or can I leave everything as it is?",
        a: "Nothing is required on your part: we can intervene even if nothing has been sorted beforehand, which is common for a house occupied for several decades. If you have the time, separating what you want to keep makes the day easier to organise, but it's not a condition for getting a quote or for the intervention to run smoothly. Sorting of valuable items is handled by the team on site either way.",
      },
      {
        q: "How does the estimate work when several heirs are involved?",
        a: "When several heirs need to weigh in on what happens to the furniture, we present the items identified as potentially valuable before any removal decision is made, giving the family time to discuss. The proposed amount, if any, can be shared with everyone involved before being accepted. This step sometimes takes longer than an individual decision, which we naturally factor into the intervention's schedule.",
      },
      {
        q: "Does sorting items extend the price or the timeline of the intervention?",
        a: "Examining potentially valuable items is an integral part of our method and doesn't incur any extra cost: it's included in the normal course of the intervention. Conversely, any buyback is deducted from the final quote. The timeline mainly varies according to the property's actual volume and access, not according to the presence of items to examine, which happens as removal progresses rather than as a separate step.",
      },
      {
        q: "Is the garden or outdoor space included in a house clearance?",
        a: "A house clearance covers the property's contents: living areas, cellar, attic, garage and outbuildings such as a garden shed. Maintaining the garden itself — mowing, pruning, removing green waste — isn't part of it and falls to a different type of provider. If the grounds contain garden furniture, tools or items stored outdoors, however, these are taken into account in the estimate, just like the rest of the property.",
      },
    ],
    finalCta: {
      title: "Ready to get your quote?",
      text: "Contact us for a free, no-obligation estimate.",
      quote: "Free quote",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  return (
    <article>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        }}
      />

      <section className="relative overflow-hidden bg-sand text-ink">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(900px 480px at 85% -10%, rgba(27,75,51,0.06), transparent 55%)" }} />
        <div className="relative mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-24">
          <h1 className="max-w-2xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-7 py-3.5 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
              <PhoneIcon className="h-5 w-5" />
              {c.ctaCall}
            </a>
            <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink/15 bg-white px-7 py-3.5 font-semibold text-ink transition hover:border-accent hover:text-accent">
              {c.ctaQuote}
              <ArrowRightIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[800px] px-6 lg:px-10 space-y-8">
          <div>
            <p className="leading-relaxed text-neutral-700">{c.intro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s1.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s1.p1}</p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s1.p2}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s2.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s2.introBefore}
              <Link href="/blog/vide-maison-succession-bruxelles" className="font-semibold text-accent underline underline-offset-4">
                {c.s2.introLink}
              </Link>
              {c.s2.introAfter}
            </p>
            <ul className="mt-4 space-y-2">
              {c.s2.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s3.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s3.intro}</p>
            <ol className="mt-4 space-y-4">
              {c.s3.steps.map(([title, text], i) => (
                <li key={title} className="flex gap-4 rounded-2xl border border-sand-line bg-sand p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-bold text-ink">{title}</p>
                    <p className="mt-1 text-sm text-neutral-600">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-4 leading-relaxed text-neutral-700">{c.s3.outro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s4.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s4.p1Before}
              <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
                {c.s4.p1Link}
              </Link>
              {c.s4.p1After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s4.p2}</p>
          </div>

          <div className="rounded-3xl border border-sand-line bg-ink p-8 text-white">
            <h2 className="font-serif text-2xl font-extrabold">{c.ctaBox.title}</h2>
            <p className="mt-2 text-white/75">{c.ctaBox.text}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-6 py-3 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
                <PhoneIcon className="h-5 w-5" />
                {c.ctaBox.call}
              </a>
              <Link href="/vide-maison/uccle" className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/20">
                {c.ctaBox.pageLink}
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s5.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s5.p1Before}
              <Link href="/rachat-objets" className="font-semibold text-accent underline underline-offset-4">
                {c.s5.p1Link1}
              </Link>
              {c.s5.p1Mid}
              <Link href="/blog/reconnaitre-objet-valeur-rachat" className="font-semibold text-accent underline underline-offset-4">
                {c.s5.p1Link2}
              </Link>
              {c.s5.p1After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s5.p2Before}
              <Link href="/blog/succession-partager-meubles-heritiers" className="font-semibold text-accent underline underline-offset-4">
                {c.s5.p2Link}
              </Link>
              {c.s5.p2After}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s6.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s6.p1}</p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s6.p2Before}
              <Link href="/blog/checklist-avant-vide-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s6.p2Link}
              </Link>
              {c.s6.p2After}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s7.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s7.p1}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s8.title}</h2>
            <ul className="mt-4 space-y-2">
              {c.s8.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-sand-line bg-sand p-6">
            <h2 className="font-serif text-xl font-extrabold text-ink">{c.retenirTitle}</h2>
            <ul className="mt-4 space-y-2">
              {c.retenir.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-neutral-700">
                  <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s9.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s9.pBefore}
              <Link href="/vide-maison/uccle" className="font-semibold text-accent underline underline-offset-4">
                {c.s9.pLink1}
              </Link>
              {c.s9.pMid}
              <Link href="/debarras-cave-grenier-garage" className="font-semibold text-accent underline underline-offset-4">
                {c.s9.pLink2}
              </Link>
              {c.s9.pMid2}
              <Link href="/vide-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s9.pLink3}
              </Link>
              {c.s9.pAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.faqTitle}</h2>
            <div className="mt-4 space-y-4">
              {c.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-sand-line bg-ink p-8 text-white">
            <h2 className="font-serif text-2xl font-extrabold">{c.finalCta.title}</h2>
            <p className="mt-2 text-white/75">{c.finalCta.text}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-6 py-3 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
                <PhoneIcon className="h-5 w-5" />
                {c.ctaCall}
              </a>
              <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/20">
                <CheckCircleIcon className="h-5 w-5" />
                {c.finalCta.quote}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </article>
  );
}
